import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { APISearchSelectRemoteResults } from "@/components/ui/select/api-search-select";
import { useDebounce } from "@/hooks/use-debounce";
import type { CPTCode, CandidateModifier, ICDTenCode, ModifierCode, PlaceOfServiceCode } from "@/types/medical-codes";
import { Page } from "@/types/pagination";
import { getCandidateModifiers, lookupIcdTenCodes, lookupModifiers, searchCPTCodes, searchICD10Codes, searchModifiers, searchPlacesOfService } from "./medical-codes";
export const medicalCodeQueryKeys = {
  all: ["medical-codes"] as const,
  cpt: () => [...medicalCodeQueryKeys.all, "cpt"] as const,
  icd10: () => [...medicalCodeQueryKeys.all, "icd-10"] as const,
  modifiers: () => [...medicalCodeQueryKeys.all, "modifiers"] as const,
  placesOfService: () => [...medicalCodeQueryKeys.all, "places-of-service"] as const,
  searchCPT: (params: {
    query: string;
    limit?: number;
  }) => [...medicalCodeQueryKeys.cpt(), "search", {
    ...params
  }] as const,
  searchICD10Infinite: (params: {
    query: string;
    perPage: number;
  }) => [...medicalCodeQueryKeys.icd10(), "search-infinite", {
    ...params
  }] as const,
  searchModifiers: (params: {
    query: string;
    limit?: number;
  }) => [...medicalCodeQueryKeys.modifiers(), "search", {
    ...params
  }] as const,
  candidateModifiers: (code: string) => [...medicalCodeQueryKeys.modifiers(), "candidates", code] as const,
  // `codes` must arrive normalized (see `normalizeModifierCodes`): the array is
  // part of the key, so an unsorted or mixed-case list would cache the same
  // lookup under several keys and refetch whenever the editor reorders lines.
  modifierDescriptions: (codes: string[]) => [...medicalCodeQueryKeys.modifiers(), "descriptions", codes] as const,
  // Normalized for the same reason as `modifierDescriptions` above.
  icd10Descriptions: (codes: string[]) => [...medicalCodeQueryKeys.icd10(), "descriptions", codes] as const,
  searchPlacesOfService: (params: {
    query: string;
  }) => [...medicalCodeQueryKeys.placesOfService(), "search", {
    ...params
  }] as const
};
export const medicalCodeQueryOptions = {
  searchCPT: (query: string, limit = 20) => ({
    queryKey: medicalCodeQueryKeys.searchCPT({
      query,
      limit
    }),
    queryFn: () => searchCPTCodes({
      query,
      limit
    }),
    select: (data: CPTCode[] | null) => data ?? []
  }),
  // Limit defaults high because the modifier set is small and an empty query
  // returns the full browsable list (the dropdown shows them all on focus).
  searchModifiers: (query: string, limit = 100) => ({
    queryKey: medicalCodeQueryKeys.searchModifiers({
      query,
      limit
    }),
    queryFn: () => searchModifiers({
      query,
      limit
    }),
    select: (data: ModifierCode[] | null) => data ?? []
  }),
  // Candidate modifiers are per-code (not query-filtered server-side), so the
  // editor caches the full set by code and filters by the typed query in-memory.
  candidateModifiers: (code: string) => ({
    queryKey: medicalCodeQueryKeys.candidateModifiers(code),
    queryFn: () => getCandidateModifiers(code),
    select: (data: CandidateModifier[] | null) => data ?? []
  }),
  // Exact batch lookup of the modifier codes on a document, so the printed
  // Super Bill / note can label each one. Codes must be normalized by the
  // caller — the array is the cache key.
  modifierDescriptions: (codes: string[]) => ({
    queryKey: medicalCodeQueryKeys.modifierDescriptions(codes),
    queryFn: () => lookupModifiers(codes),
    select: (data: ModifierCode[] | null) => data ?? []
  }),
  // Exact batch lookup of the ICD-10 codes on a document, so each service
  // block can name its diagnoses. Codes must be normalized by the caller.
  icd10Descriptions: (codes: string[]) => ({
    queryKey: medicalCodeQueryKeys.icd10Descriptions(codes),
    queryFn: () => lookupIcdTenCodes(codes),
    select: (data: ICDTenCode[] | null) => data ?? []
  }),
  searchPlacesOfService: (query: string) => ({
    queryKey: medicalCodeQueryKeys.searchPlacesOfService({
      query
    }),
    queryFn: () => searchPlacesOfService(query),
    select: (data: PlaceOfServiceCode[] | null) => data ?? []
  })
};
export const ICD10_SEARCH_PER_PAGE = 20;
const EMPTY_ICD10_PAGE: Page<ICDTenCode> = {
  data: [],
  total: 0
};

/**
 * Paged ICD-10 search for the code pickers' infinite scroll, returned in the
 * shape `APISearchSelect`'s `remote` prop takes. Follows the platform's
 * searchable-picker pattern (`useEmailTemplatesInfinite` / `LoadTemplateMenu`):
 * the typed term is debounced into the query key, pages are fetched with
 * `useInfiniteQuery`, and paging stops once `total` rows are loaded.
 *
 * `excludeCodes` drops already-picked codes from the list (lab order flows).
 * Paging follows the server's `total`, so the filter can't end it early.
 */
export function useIcd10Search(t0) {
  const $ = _c(34);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    excludeCodes: t2
  } = t1;
  let t3;
  if ($[2] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const excludeCodes = t3;
  const [term, setTerm] = useState("");
  let t4;
  if ($[4] !== term) {
    t4 = term.trim();
    $[4] = term;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const typed = t4;
  const query = useDebounce(typed, 300);
  let t5;
  if ($[6] !== query) {
    t5 = medicalCodeQueryKeys.searchICD10Infinite({
      query,
      perPage: ICD10_SEARCH_PER_PAGE
    });
    $[6] = query;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== query) {
    t6 = async t7 => {
      const {
        pageParam
      } = t7;
      return (await searchICD10Codes({
        query,
        page: pageParam,
        perPage: ICD10_SEARCH_PER_PAGE
      })) ?? EMPTY_ICD10_PAGE;
    };
    $[8] = query;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = query.length > 0;
  let t8;
  if ($[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      getNextPageParam: _temp2,
      initialPageParam: 1,
      enabled: t7,
      placeholderData: keepPreviousData
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery(t8);
  let t9;
  if ($[14] !== excludeCodes) {
    t9 = excludeCodes.join("\n");
    $[14] = excludeCodes;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const excludeKey = t9;
  data?.pages;
  let t10;
  bb0: {
    if (!typed || !query) {
      let t11;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = [];
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      t10 = t11;
      break bb0;
    }
    let t11;
    if ($[17] !== excludeKey) {
      t11 = new Set(excludeKey ? excludeKey.split("\n") : []);
      $[17] = excludeKey;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    const excluded = t11;
    let t12;
    if ($[19] !== data?.pages || $[20] !== excluded) {
      let t13;
      if ($[22] !== excluded) {
        t13 = code => !excluded.has(code.code);
        $[22] = excluded;
        $[23] = t13;
      } else {
        t13 = $[23];
      }
      t12 = (data?.pages.flatMap(_temp3) ?? []).filter(t13);
      $[19] = data?.pages;
      $[20] = excluded;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t10 = t12;
  }
  const results = t10;
  let t11;
  if ($[24] !== fetchNextPage || $[25] !== hasNextPage || $[26] !== isFetchingNextPage) {
    t11 = () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage({
          cancelRefetch: false
        });
      }
    };
    $[24] = fetchNextPage;
    $[25] = hasNextPage;
    $[26] = isFetchingNextPage;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const onLoadMore = t11;
  const isLoading = typed !== query || isFetching && !isFetchingNextPage;
  const t12 = !!hasNextPage;
  let t13;
  if ($[28] !== isFetchingNextPage || $[29] !== isLoading || $[30] !== onLoadMore || $[31] !== results || $[32] !== t12) {
    t13 = {
      results,
      onSearchTermChange: setTerm,
      isLoading,
      hasNextPage: t12,
      isFetchingNextPage,
      onLoadMore
    };
    $[28] = isFetchingNextPage;
    $[29] = isLoading;
    $[30] = onLoadMore;
    $[31] = results;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function _temp3(page_0) {
  return page_0.data;
}
function _temp2(lastPage, allPages) {
  const loadedCount = allPages.reduce(_temp, 0);
  return loadedCount < lastPage.total ? allPages.length + 1 : undefined;
}
function _temp(sum, page) {
  return sum + page.data.length;
}
const toCodeOption = (code: ICDTenCode): CPTCode => ({
  code: code.code,
  description: code.shortDesc || code.longDesc || ""
});

/**
 * `useIcd10Search` mapped to the `{code, description}` rows the creatable code
 * comboboxes (Super Bill, service defaults, scribe answers) expect.
 */
export function useIcd10CodeOptionSearch() {
  const $ = _c(5);
  const remote = useIcd10Search();
  let t0;
  if ($[0] !== remote.results) {
    t0 = remote.results.map(toCodeOption);
    $[0] = remote.results;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const results = t0;
  let t1;
  if ($[2] !== remote || $[3] !== results) {
    t1 = {
      ...remote,
      results
    };
    $[2] = remote;
    $[3] = results;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}